import type {
  SupportCenterDemandType,
  SupportCenterDemandSelectionItem,
} from './supportCenterTriage'

/** Prontuário de atendimento psicológico (agenda) — persistido no Supabase. */

export const SUPPORT_CENTER_PSYCH_EMOTIONAL_STATES = [
  'Estabilizado',
  'Parcialmente estabilizado',
  'Em crise',
  'Sem avaliação possível',
] as const
export type SupportCenterPsychEmotionalState =
  (typeof SUPPORT_CENTER_PSYCH_EMOTIONAL_STATES)[number]

export const SUPPORT_CENTER_PSYCH_RISK_LEVELS = [
  'Sem risco',
  'Risco leve',
  'Risco moderado',
  'Risco alto',
] as const
export type SupportCenterPsychRiskLevel = (typeof SUPPORT_CENTER_PSYCH_RISK_LEVELS)[number]

export const SUPPORT_CENTER_PSYCH_REFERRALS = [
  'Nenhum',
  'Psiquiatria',
  'Assistência social',
  'UBS / Atenção básica',
  'CAPS',
  'Continuidade na assistência',
  'Outro',
] as const
export type SupportCenterPsychReferral = (typeof SUPPORT_CENTER_PSYCH_REFERRALS)[number]

export type SupportCenterAttendanceDemandType = Pick<
  SupportCenterDemandType,
  'id' | 'name' | 'requires_notes'
>
export type SupportCenterAttendanceSelectedDemandType = SupportCenterAttendanceDemandType & {
  notes: string | null
}
export type SupportCenterAttendanceDemandSelection = SupportCenterDemandSelectionItem[]

export type SupportCenterPsychAttendanceFormState = {
  demand_selections: SupportCenterAttendanceDemandSelection
  medications: string[]
  demand_notes: string
  intervention_notes: string
  patient_response: string
  emotional_state: SupportCenterPsychEmotionalState | ''
  risk_indicator: SupportCenterPsychRiskLevel | ''
  referral: SupportCenterPsychReferral | ''
  next_contact_date: string
  additional_notes: string
}

export type SupportCenterPsychAttendancePayload = {
  appointment_id: string
  demand_selections: SupportCenterAttendanceDemandSelection
  medications: string[]
  demand_notes: string
  intervention_notes: string
  patient_response: string | null
  emotional_state: SupportCenterPsychEmotionalState
  risk_indicator: SupportCenterPsychRiskLevel
  referral: SupportCenterPsychReferral | null
  next_contact_date: string | null
  additional_notes: string | null
}

export const EMPTY_PSYCH_ATTENDANCE_FORM: SupportCenterPsychAttendanceFormState = {
  demand_selections: [],
  medications: [],
  demand_notes: '',
  intervention_notes: '',
  patient_response: '',
  emotional_state: '',
  risk_indicator: '',
  referral: '',
  next_contact_date: '',
  additional_notes: '',
}

/** Campos do prontuário para leitura (detalhe / atendimento realizado). */
export const SUPPORT_CENTER_PSYCH_VIEW_FIELDS = [
  { key: 'demand_notes', label: 'Demanda trazida' },
  { key: 'intervention_notes', label: 'Intervenção realizada' },
  { key: 'patient_response', label: 'Resposta do paciente' },
  { key: 'emotional_state', label: 'Estado emocional' },
  { key: 'risk_indicator', label: 'Indicador de risco' },
  { key: 'referral', label: 'Encaminhamento' },
  { key: 'next_contact_date', label: 'Próximo contato' },
  { key: 'additional_notes', label: 'Observações adicionais' },
] as const

export type SupportCenterPsychAttendanceViewFieldKey =
  (typeof SUPPORT_CENTER_PSYCH_VIEW_FIELDS)[number]['key']

/** Prontuário psicológico carregado para exibição (detalhe / atendimento realizado). */
export type SupportCenterPsychAttendanceView = Record<
  SupportCenterPsychAttendanceViewFieldKey,
  string | null
>

/** Prontuário psicológico com metadado de envio (rascunho vs enviado). */
export type SupportCenterPsychAttendanceRecord = SupportCenterPsychAttendanceView & {
  demand_selections: SupportCenterAttendanceDemandSelection
  demand_types: SupportCenterAttendanceSelectedDemandType[]
  medications: string[]
  submitted_at: string | null
}

/** Texto de atendimento (observações / orientação) com metadado de envio. */
export type SupportCenterAttendanceNotesRecord = {
  demand_selections: SupportCenterAttendanceDemandSelection
  demand_types: SupportCenterAttendanceSelectedDemandType[]
  notes: string | null
  submitted_at: string | null
}
